import { useEffect, useState } from "react";

/**
 * Page-scoped navbar surface detection.
 *
 * A page renders <NavSurfaceSentinel /> at the boundary where the dark hero/visual
 * surface ends and the white page surface begins. While the sentinel is *above*
 * the navbar zone (i.e. white surface is now under the navbar), `data-nav-on-light`
 * is set on <html>; when the sentinel scrolls back down (dark hero under navbar),
 * the flag is removed.
 *
 * Stable hysteresis is achieved by using two thresholds: enter at -8px (sentinel
 * 8px above the navbar bottom) and exit at +12px. This eliminates flicker on
 * iOS Safari where rubber-band scrolling can otherwise toggle state rapidly.
 */
export function useNavSurfaceController(sentinelId: string, enabled: boolean) {
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;

    const html = document.documentElement;
    const NAV_ZONE = 72; // approximate navbar bottom edge from top

    let onLight = false;
    let raf = 0;

    const measure = () => {
      raf = 0;
      const el = document.getElementById(sentinelId);
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      // Hysteresis: enter dark-glass (on-light) once sentinel is 8px above nav zone,
      // exit only when it scrolls 12px back below.
      const next = onLight ? top < NAV_ZONE + 12 : top < NAV_ZONE - 8;
      if (next !== onLight) {
        onLight = next;
        if (next) html.setAttribute("data-nav-on-light", "");
        else html.removeAttribute("data-nav-on-light");
      }
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
      html.removeAttribute("data-nav-on-light");
    };
  }, [sentinelId, enabled]);
}

/**
 * Reactive read of the on-light flag. Used by Navbar to switch to dark-glass
 * styling while the navbar overlaps a bright page surface.
 */
export function useNavOnLight() {
  const [onLight, setOnLight] = useState(
    typeof document !== "undefined" && document.documentElement.hasAttribute("data-nav-on-light"),
  );

  useEffect(() => {
    const html = document.documentElement;
    const sync = () => setOnLight(html.hasAttribute("data-nav-on-light"));
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(html, { attributes: true, attributeFilter: ["data-nav-on-light"] });
    return () => obs.disconnect();
  }, []);

  return onLight;
}
